import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Collapse, Switch, Tag, Tooltip } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  PlusCircleOutlined,
  HolderOutlined,
  CaretRightOutlined,
} from "@ant-design/icons";

import './styles.scss'
import { activeParameters, IGroupParam, Param, swapParameters } from "../../../api/paramCRM/paramCRMApi";
import useDebounce from "../../../hook/useDebounce";
import useLoading from "../../../hook/useLoading";
import useToast from "../../../hook/useToast";

// export interface Param {
//     id: number;
//     code: string;
//     name: string;
//     fieldCode: string;
//     fieldLabel: string;
//     sortOrder: number;
// }

// export interface IGroupParam {
//     id: number;
//     title: string;
//     name: string;
//     sortOrder: number;
//     params: Param[];
// }

export interface IParamState {
  enabled: boolean;
}

interface Props {
  data?: IGroupParam[];
  onView?: (param: Param, group: IGroupParam) => void;
  onEdit?: (param: Param, group: IGroupParam) => void;
  onAdd?: (group: IGroupParam) => void;
  onStatusChange?: (
    param: Param,
    group: IGroupParam,
    checked: boolean
  ) => void;
  onOrderChange?: (groupId: number, paramIds: number[]) => void;
  gListGroupParam: (...args: any) => void
}

const ShowCRMParam: React.FC<Props> = ({
  data,
  onView,
  onEdit,
  onAdd,
  onStatusChange,
  onOrderChange,
  gListGroupParam,
}) => {
  const pushLoading = useLoading()
  const pushToast = useToast()

  // State riêng cho trạng thái Switch
  const [paramStates, setParamStates] = useState<
    Record<string, IParamState>
  >({});

  // State lưu thứ tự params theo từng group
  const [groups, setGroups] = useState<IGroupParam[]>([]);

  // Thông tin param đang được kéo thả
  const dragRef = useRef<{
    groupId: number;
    paramId: number;
  } | null>(null);

  // const gSwapParameters = useDebounce(async (recordId: number, listFields?: number[]) => {
  //   pushLoading(true)
  //   const result = await swapParameters(recordId, listFields)
  //   if (result?.status) {
  //   setGroups(updatedGroups);

  //     gListGroupParam()
  //     pushToast(result.message, 'success')
  //   } else {
  //     pushToast(result.message, 'error')

  //   }
  //   pushLoading(false)
  // }, 1000)

  const updateActiveParameters = useDebounce(async (group: IGroupParam, param: Param, checked: boolean) => {
    pushLoading(true)
    const key = getParamKey(group.id, param.id);
    const result = await activeParameters(param.code)
    if (result?.status) {
      gListGroupParam()
      setParamStates((prev) => ({
        ...prev,
        [key]: { enabled: checked },
      }));
      pushToast(result.message, "success")
    } else {
      pushToast(result.message, "error")
    }
    pushLoading(false)
  }, 1000)

  useEffect(() => {
    data && setGroups(
      [...data]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((group) => ({
          ...group,
          params: [...group.params].sort(
            (a, b) => a.sortOrder - b.sortOrder
          ),
        }))
    );
  }, [data]);

  // id của param có thể trùng ở các nhóm khác nhau
  const getParamKey = (groupId: number, paramId: number) =>
    `${groupId}:${paramId}`;

  const getEnabled = (groupId: number, paramId: number) =>
    paramStates[getParamKey(groupId, paramId)]?.enabled ?? true;

  const handleSwitch = (
    group: IGroupParam,
    param: Param,
    checked: boolean
  ) => {

    updateActiveParameters(group, param, checked)
    onStatusChange?.(param, group, checked);
  };

  const handleDragStart = (
    groupId: number,
    paramId: number
  ) => {
    dragRef.current = { groupId, paramId };
  };

  const handleDrop = async (
    targetGroupId: number,
    targetParamId: number
  ) => {
    const dragging = dragRef.current;
    dragRef.current = null;

    if (!dragging || dragging.groupId !== targetGroupId) {
      return;
    }

    if (dragging.paramId === targetParamId) {
      return;
    }

    const updatedGroups = groups.map((group) => {
      if (group.id !== targetGroupId) return group;

      const items = [...group.params];

      const from = items.findIndex(
        (p) => p.id === dragging.paramId
      );

      const to = items.findIndex(
        (p) => p.id === targetParamId
      );

      if (from < 0 || to < 0) return group;

      const [moved] = items.splice(from, 1);
      items.splice(to, 0, moved);

      const updated = items.map((item, index) => ({
        ...item,
        sortOrder: index + 1,
      }));

      return {
        ...group,
        params: updated,
      };
    });

    // Cập nhật state - vì load lại api nên k cần setGroup
    // setGroups(updatedGroups);

    // Log toàn bộ dữ liệu sau khi kéo thả
    // console.log("Groups sau khi kéo thả:", updatedGroups);

    // Log riêng danh sách param của nhóm vừa kéo thả
    const currentGroup = updatedGroups.find(
      (group) => group.id === targetGroupId
    );

    if (currentGroup) {
      pushLoading(true)
      const result = await swapParameters(currentGroup.id,
        currentGroup.params.map((item) => item.id))
      if (result?.status) {
        setGroups(updatedGroups);

        gListGroupParam()
        pushToast(result.message, 'success')
      } else {
        pushToast(result.message, 'error')

      }
      pushLoading(false)

      // gSwapParameters(currentGroup.id,
      //   currentGroup.params.map((item) => item.id))
      // Callback gửi danh sách ID theo thứ tự mới
      // onOrderChange?.(
      //   currentGroup.id,
      //   currentGroup.params.map((item) => item.id)
      // );
    }
  };

  const collapseItems = useMemo(
    () =>
      groups.map((group) => ({
        key: String(group.id),

        label: (
          <div className="custom-page-css flex items-center gap-3">
            <span className="custom-page-css font-500 text-344054">
              {group.title}
            </span>

            <Tag
              color="orange"
              bordered={false}
              className="custom-page-css rounded-lg"
            >
              {group.params.length} params
            </Tag>
          </div>
        ),

        extra: (
          <Tooltip title="Thêm trường">
            <Button
              type="text"
              size="small"
              icon={
                <PlusCircleOutlined
                  style={{ color: "#1677FF", fontSize: 17 }}
                />
              }
              onClick={(e) => {
                e.stopPropagation();
                onAdd?.(group);
              }}
            />
          </Tooltip>
        ),

        children: (
          <div className="custom-page-css flex flex-col">
            {group.params.map((param: Param, index: number) => (
              <div
                key={`${group.id}-${param.id}`}
                className={`flex items-center justify-between px-4 py-3 min-h-[52px] ${index % 2 === 0
                  ? "bg-[#F8FAFC]"
                  : "bg-white"
                  }`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(group.id, param.id)}
              >
                <div className="custom-page-css flex items-center gap-4 min-w-0">
                  <span
                    draggable
                    className="custom-page-css cursor-grab text-[#98A2B3]"
                    onDragStart={() =>
                      handleDragStart(group.id, param.id)
                    }
                    onDragEnd={() => {
                      dragRef.current = null;
                    }}
                  >
                    <HolderOutlined />
                  </span>

                  <span className="custom-page-css font-500 text-344054 truncate">
                    {param.fieldLabel || param.name}
                  </span>
                </div>

                <div className="custom-page-css flex items-center gap-3 shrink-0">
                  <Tooltip title="Xem">
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => onView?.(param, group)}
                    />
                  </Tooltip>

                  <Tooltip title="Chỉnh sửa">
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => onEdit?.(param, group)}
                    />
                  </Tooltip>

                  <Switch
                    checked={param.is_active}
                    onChange={(checked) =>
                      handleSwitch(group, param, checked)
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        ),
      })),
    [groups, paramStates, onView, onEdit, onAdd, onStatusChange, onOrderChange]
  );

  return (
    <Collapse
      defaultActiveKey={["12"]}
      items={collapseItems}
      expandIcon={({ isActive }) => <CaretRightOutlined style={{ color: '#8c8c8c' }} rotate={isActive ? 90 : 0} />}
      className="custom-page-css  crm-param-collapse"
    />
  );
};

export default ShowCRMParam;